import { useEffect, useRef, useState } from 'react';
import { niceTicks } from '../../charts/chartUtils';

/**
 * The review deck's Technical Support charts, drawn the way the Autotask
 * dashboards the account team already shows clients draw them: a two-line
 * created-vs-completed trend with a dot on every month, and horizontal bar
 * charts with a colour per bar and the total in the corner.
 *
 * Their own SVG rather than the app's LineChart/BarChart: those are tuned for
 * the app's dashboards (sparse axis labels, one series colour, hover-only
 * detail), and a slide is read from across a room.
 */

/**
 * Width AND height — a slide hands each chart a box, and the chart fills it.
 *
 * Read off the layout box (offsetWidth/Height) rather than
 * getBoundingClientRect: Presentation mode scales the whole slide with a CSS
 * transform, and a client rect is the scaled size. Measuring that drew the
 * SVG a scale-factor too big, so the chart spilled past its card.
 */
function useBox<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const read = () => {
      const w = node.offsetWidth;
      const h = node.offsetHeight;
      setBox((prev) => (Math.abs(prev.w - w) > 0.5 || Math.abs(prev.h - h) > 0.5 ? { w, h } : prev));
    };
    const observer = new ResizeObserver(read);
    observer.observe(node);
    read();
    return () => observer.disconnect();
  }, []);
  return [ref, box] as const;
}

/* The Autotask dashboard's palette, in its order. */
const BAR_COLOURS = ['#6ab0de', '#6f8bd8', '#5fc4c0', '#b6e06a', '#f9c851', '#49a8d8', '#5a7fd9'];
const CREATED = '#3a5bb5';
const COMPLETED = '#2ea8de';

/* ------------------------------------------------------------------ trend */

export interface TrendMonth {
  /** YYYY-MM */
  month: string;
  created: number;
  completed: number;
}

const monthLabel = (key: string) => {
  const [y, m] = key.split('-').map(Number) as [number, number];
  return new Date(y, m - 1, 1).toLocaleDateString([], { month: 'short', year: 'numeric' });
};

/**
 * Created vs. completed by month: two lines with a dot on every month, every
 * month labelled, a vertical rule between months, and a legend that carries
 * each line's total. Clicking anywhere in a month's column selects it.
 */
export function TicketTrendChart({ months, onSelect }: { months: TrendMonth[]; onSelect?: (month: string) => void }) {
  const [ref, box] = useBox<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const pad = { top: 12, right: 16, bottom: 28, left: 40 };
  const w = Math.max(box.w, 240);
  const h = Math.max(box.h, 160);
  const innerW = w - pad.left - pad.right;
  const innerH = h - pad.top - pad.bottom;
  const max = Math.max(1, ...months.flatMap((m) => [m.created, m.completed]));
  const ticks = niceTicks(0, max, 4);
  const top = Math.max(...ticks);
  const band = innerW / Math.max(months.length, 1);
  const x = (i: number) => pad.left + band * i + band / 2;
  const y = (v: number) => pad.top + innerH - (v / top) * innerH;
  const line = (k: 'created' | 'completed') => months.map((m, i) => `${i ? 'L' : 'M'}${x(i)},${y(m[k])}`).join(' ');
  const total = (k: 'created' | 'completed') => months.reduce((n, m) => n + m[k], 0);

  return (
    <div className="rv-chart">
      <div className="rv-chart-plot" ref={ref}>
        {box.w > 0 && (
          <svg width={w} height={h} role="img" aria-label="Tickets created and completed by month" onMouseLeave={() => setHover(null)}>
            {ticks.map((t) => (
              <g key={t}>
                <line x1={pad.left} x2={w - pad.right} y1={y(t)} y2={y(t)} className="rv-grid" />
                <text x={pad.left - 8} y={y(t)} className="rv-tick" textAnchor="end" dominantBaseline="middle">
                  {t}
                </text>
              </g>
            ))}
            {months.map((m, i) => (
              <g key={m.month}>
                <line x1={pad.left + band * (i + 1)} x2={pad.left + band * (i + 1)} y1={pad.top} y2={pad.top + innerH} className="rv-grid" />
                <text x={x(i)} y={h - 8} className="rv-tick" textAnchor="middle">
                  {monthLabel(m.month)}
                </text>
              </g>
            ))}
            <line x1={pad.left} x2={pad.left} y1={pad.top} y2={pad.top + innerH} className="rv-grid" />
            <line x1={pad.left} x2={w - pad.right} y1={pad.top + innerH} y2={pad.top + innerH} className="rv-axis" />

            <path d={line('created')} fill="none" stroke={CREATED} strokeWidth={1.5} />
            <path d={line('completed')} fill="none" stroke={COMPLETED} strokeWidth={1.5} />
            {months.map((m, i) => (
              <g key={`p-${m.month}`}>
                <circle cx={x(i)} cy={y(m.created)} r={hover === i ? 6.5 : 5} fill={CREATED} />
                <circle cx={x(i)} cy={y(m.completed)} r={hover === i ? 6.5 : 5} fill={COMPLETED} />
              </g>
            ))}

            {/* A whole-column hit target per month, so a click anywhere in it
                opens that month. */}
            {months.map((m, i) => (
              <rect
                key={`hit-${m.month}`}
                x={pad.left + band * i}
                y={pad.top}
                width={band}
                height={innerH}
                fill="transparent"
                style={onSelect ? { cursor: 'pointer' } : undefined}
                onMouseEnter={() => setHover(i)}
                onClick={onSelect ? () => onSelect(m.month) : undefined}
              >
                <title>
                  {monthLabel(m.month)}: {m.created} created, {m.completed} completed
                </title>
              </rect>
            ))}
          </svg>
        )}
      </div>
      <div className="rv-legend">
        <span>
          <i style={{ background: CREATED }} /> Created Ticket Count ({total('created')})
        </span>
        <span>
          <i style={{ background: COMPLETED }} /> Completed Ticket Count ({total('completed')})
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- bars */

/**
 * A ranked horizontal bar chart in a card: title, total top right, a colour
 * per bar, value axis along the bottom with vertical rules. Rows beyond what
 * fits legibly are folded into one "All others" bar so the total still adds up.
 */
export function TicketBarsCard({
  title,
  rows,
  empty,
  maxRows = 12,
  onSelect,
}: {
  title: string;
  rows: { label: string; value: number }[];
  empty: string;
  maxRows?: number;
  /** Makes each bar clickable. Gets the bar's heading and the labels behind
   *  it — one, or every label folded into "All others". */
  onSelect?: (heading: string, labels: string[]) => void;
}) {
  const [ref, box] = useBox<HTMLDivElement>();
  const sorted = [...rows].sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
  const shown: { label: string; value: number; labels: string[] }[] =
    sorted.length > maxRows
      ? [
          ...sorted.slice(0, maxRows - 1).map((r) => ({ ...r, labels: [r.label] })),
          {
            label: `All others (${sorted.length - maxRows + 1})`,
            value: sorted.slice(maxRows - 1).reduce((n, r) => n + r.value, 0),
            labels: sorted.slice(maxRows - 1).map((r) => r.label),
          },
        ]
      : sorted.map((r) => ({ ...r, labels: [r.label] }));
  const total = rows.reduce((n, r) => n + r.value, 0);

  const labelW = Math.min(190, Math.max(90, box.w * 0.28));
  const pad = { top: 4, right: 14, bottom: 20, left: labelW + 8 };
  const w = Math.max(box.w, 240);
  const h = Math.max(box.h, 80);
  const innerW = w - pad.left - pad.right;
  const innerH = h - pad.top - pad.bottom;
  const ticks = niceTicks(0, Math.max(1, ...shown.map((r) => r.value)), 5);
  const top = Math.max(...ticks);
  const rowH = innerH / Math.max(shown.length, 1);
  const barH = Math.max(4, Math.min(rowH * 0.78, 16));
  const font = Math.max(9, Math.min(12, rowH * 0.72));
  const x = (v: number) => pad.left + (v / top) * innerW;

  return (
    <section className="rv-card">
      <header className="rv-card-head">
        <h4>{title}</h4>
        <span>Total: {total}</span>
      </header>
      <div className="rv-card-plot" ref={ref}>
        {shown.length === 0 ? (
          <p className="rv-empty">{empty}</p>
        ) : (
          box.w > 0 && (
            <svg width={w} height={h} role="img" aria-label={`${title}: total ${total}`}>
              {ticks.map((t) => (
                <g key={t}>
                  <line x1={x(t)} x2={x(t)} y1={pad.top} y2={pad.top + innerH} className="rv-grid" />
                  <text x={x(t)} y={h - 6} className="rv-tick" textAnchor="middle">
                    {t}
                  </text>
                </g>
              ))}
              {shown.map((r, i) => {
                const cy = pad.top + rowH * i + rowH / 2;
                return (
                  <g
                    key={r.label}
                    className={onSelect ? 'rv-bar-pick' : undefined}
                    onClick={onSelect ? () => onSelect(r.label, r.labels) : undefined}
                  >
                    {/* The whole row is the hit target, label and gap included. */}
                    {onSelect && <rect x={0} y={pad.top + rowH * i} width={w} height={rowH} fill="transparent" />}
                    <line x1={pad.left} x2={w - pad.right} y1={pad.top + rowH * (i + 1)} y2={pad.top + rowH * (i + 1)} className="rv-grid rv-grid-faint" />
                    <text x={pad.left - 6} y={cy} className="rv-tick rv-label" style={{ fontSize: font }} textAnchor="end" dominantBaseline="middle">
                      {r.label.length > 30 ? `${r.label.slice(0, 29)}…` : r.label}
                    </text>
                    <rect x={pad.left} y={cy - barH / 2} width={Math.max(1, x(r.value) - pad.left)} height={barH} fill={BAR_COLOURS[i % BAR_COLOURS.length]}>
                      <title>
                        {r.label}: {r.value}
                      </title>
                    </rect>
                  </g>
                );
              })}
            </svg>
          )
        )}
      </div>
    </section>
  );
}
